import type { InputTableQuerySpec, InputWorkflowViewParamInput } from "@selector/shared/graphql";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CalloutIntent, DialogComponent, Modal, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import {
  createSelectionModel,
  type ISelectionModel,
  type TGridSelModelIdentifier,
} from "@/app-utils/selection-model.ts";
import type { InputColumnView } from "@/graphql.ts";
import { GenomeSelect } from "@/modules/workflow/components/parameters/GenomeSelect.tsx";
import { useSaveTableViewMutation } from "@/modules/workflow/graphql/coordinate-lists.gql.ts";
import { createUniqueKeyFromTableSpec, getTableDataSlice, type ITableDataState } from "@/redux/modules/table-data.ts";
import { createTableSpecForOrganismParent } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE, INVALID_VIEW_PATH, VIEW } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";

interface ModalGridProps {
  isOpen: boolean;
  availableGenomesIds: number[];
  onClose: (next?: boolean) => void;
  onSave: (viewId: number | null, genomeId: number | null, listId: number | null) => void;
  workflowEventId: number | undefined;
  defaultValue: {
    genomeId: number | null;
    listId: number | null;
  };
}

export const CoordinatesListModal = ({
  availableGenomesIds,
  isOpen,
  onClose,
  onSave,
  workflowEventId,
  defaultValue: { genomeId, listId },
}: ModalGridProps) => {
  const [saveCoordinatesView] = useSaveTableViewMutation();

  const { overlayProps, state } = useOverlay({ isOpen });
  const initialGenomeId = genomeId ?? (availableGenomesIds.length > 0 ? availableGenomesIds[0] : null);
  const [selectedGenomeId, setSelectedGenomeId] = useState<number | null>(initialGenomeId);
  const [selectionsByGenome, setSelectionsByGenome] = useState<Record<number, ISelectionModel["selectedIds"]>>(
    initialGenomeId != null ? { [initialGenomeId]: listId ? [listId] : [] } : {},
  );

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const columns: InputColumnView[] = getTableDataSlice(
    useSelector<IAppState, ITableDataState>((state) => state.tableData),
    useSelector<IAppState, ITableDataState>((state) => state.tableData).activeTable,
  )?.columns.map((c) => {
    return { field: c.field, rowFilter: c.filterExpression, sort: c.sort };
  });

  const rowFilter = getTableDataSlice(
    useSelector<IAppState, ITableDataState>((state) => state.tableData),
    useSelector<IAppState, ITableDataState>((state) => state.tableData).activeTable,
  )?.tableQuerySpec?.rowFilter;

  // Derive selectedListIds from selectionsByGenome
  const selectedListIds = selectedGenomeId != null ? selectionsByGenome[selectedGenomeId] || [] : [];

  const tableSpec = selectedGenomeId
    ? createTableSpecForOrganismParent(ENTITY_TYPE.COORD_LIST, selectedGenomeId)
    : undefined;

  const handleUpdate = useCallback(
    (genomeId: number, listId: number | null) => {
      if (listId != null) {
        const tableQuerySpec: InputTableQuerySpec = {
          id: listId,
          idType: DATATABLE_ID_TYPE.ENTITY,
          queryType: DATATABLE_PARENT_TYPE.COORD_LIST,
          resultType: ENTITY_TYPE.COORDINATE,
          rowFilter,
          restrictions: { refGenomeId: genomeId },
        };

        const viewInput: InputWorkflowViewParamInput = {
          viewLabel: VIEW.TABLE_VIEW.title,
          viewType: VIEW.TABLE_VIEW.type,
          path: INVALID_VIEW_PATH,
          workflowEventId,
          state: { tableView: { tableQuerySpec, columns } },
        };

        setIsSaving(true);
        saveCoordinatesView({ variables: { viewInput } })
          .then(({ data }) => {
            const viewId = data?.workflow.saveTableParamViewInput.value?.id ?? null;
            onSave(viewId, genomeId, listId);
            onClose(true);
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setIsSaving(false);
          });
      } else {
        // reset parameter to Nothing
        onSave(null, genomeId, listId);
        onClose(false);
      }
    },
    [onClose, onSave, saveCoordinatesView, workflowEventId, columns, rowFilter],
  );

  const onCoordinateListChange = useCallback(
    (selectedLists: TGridSelModelIdentifier[]) => {
      setError(null);

      // only single selection for coordinate lists
      const selectedList = selectedLists[0] ? Number(selectedLists[0]) : null;
      const selectionChanged = selectedList !== listId;

      if (selectionChanged) {
        if (selectedGenomeId == null) {
          onClose(false);
          return;
        }

        // handle onClose (async save call, closing after)
        handleUpdate(selectedGenomeId, selectedList);
      } else {
        onClose(selectedList != null);
      }
    },
    [handleUpdate, listId, onClose, selectedGenomeId],
  );

  useEffect(() => {
    // Keep internal state aligned with upstream changes, even while closed.
    const currentGenomeId = genomeId ?? (availableGenomesIds.length > 0 ? availableGenomesIds[0] : null);
    setSelectedGenomeId(currentGenomeId);
    setSelectionsByGenome(currentGenomeId != null ? { [currentGenomeId]: listId ? [listId] : [] } : {});
  }, [availableGenomesIds, genomeId, isOpen, listId]);

  if (!tableSpec) {
    return null;
  }

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        fullscreen
        title="Select Coordinate Lists"
        mainButton={{
          label: selectedListIds.length === 0 && listId ? "Remove" : "Select",
          onPress: () => {
            // we can only convert to number if we have a selection
            onCoordinateListChange(selectedListIds);
          },
          isDisabled: (selectedListIds.length < 1 && !listId) || selectedListIds.length > 1,
          isLoading: isSaving,
        }}
        actions={[
          <ColumnSelectionButton
            key="column-selection-button-placeholder"
            portalContext={createUniqueKeyFromTableSpec(tableSpec)}
          />,
        ]}
        closeFn={() => {
          // Clear per-genome selections when dialog closes
          setSelectionsByGenome({});
          setIsSaving(false);
          setError(null);
          onClose(false);
        }}
        errorCallout={
          error
            ? { intent: CalloutIntent.Danger, message: error }
            : selectedListIds.length > 1
              ? { intent: CalloutIntent.Warning, message: "Only one coordinate list can be selected" }
              : undefined
        }
        contentStyle={{ display: "grid", padding: 0, borderTop: "1px solid var(--ui-border)" }}
        infoComponent={
          <GenomeSelect
            availableGenomesIds={availableGenomesIds}
            selectedId={selectedGenomeId ?? undefined}
            onChange={setSelectedGenomeId}
          />
        }
      >
        <VStack style={{ width: "100%", height: "100%" }}>
          {/* @ts-expect-error Reported missing properties of this component are actually not required */}
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(tableSpec)}
            tableSpec={tableSpec}
            gridContext={{ tableSpec }}
            noRowsText="No coordinate lists found"
            onSelectionModelChanged={(selected) => {
              if (selectedGenomeId == null) {
                return;
              }
              setSelectionsByGenome((prev) => ({
                ...prev,
                [selectedGenomeId]: selected.selectedIds,
              }));
            }}
            tableInitializationOptions={{ initialSelectionModel: createSelectionModel(selectedListIds) }}
          />
        </VStack>
      </DialogComponent>
    </Modal>
  );
};
